import type { Maybe, Nullish } from "shared/types";

// Keys whose values must never reach LogRocket. Matched case-insensitively against JSON body
// keys (at any depth, e.g. spraypaint's `params.pin`) and URL query params.
const SENSITIVE_KEYS = [
  "access_token",
  "auth_token",
  "code",
  "current_password",
  "id_token",
  "login_code",
  "password",
  "password_confirmation",
  "pin",
  "refresh_token",
  "reset_key",
  "token",
  "two_factor_code",
];

// `state` is only sensitive in URLs: the /oauth callback carries base64-encoded tokens in it.
// JSON bodies use `state` for unrelated data, so it isn't redacted there.
const SENSITIVE_QUERY_PARAMS = new Set([...SENSITIVE_KEYS, "state"]);

const SENSITIVE_HEADERS = ["authorization", "cookie", "set-cookie"];

const REDACTED = "[REDACTED]";

const SENSITIVE_KEY_SET = new Set(SENSITIVE_KEYS);

// Used for bodies that aren't JSON, where keys can't be redacted individually.
const SENSITIVE_TEXT_REGEXP = new RegExp(SENSITIVE_KEYS.join("|"), "i");

interface SanitizableNetworkEntry {
  body?: string;
  headers: Record<string, Nullish<string>>;
  url?: string;
}

function redactValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redactValue);

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        SENSITIVE_KEY_SET.has(key.toLowerCase())
          ? REDACTED
          : redactValue(nestedValue),
      ])
    );
  }

  return value;
}

function redactBody(body: Maybe<string>): Maybe<string> {
  if (!body) return body;

  try {
    return JSON.stringify(redactValue(JSON.parse(body)));
  } catch {
    return SENSITIVE_TEXT_REGEXP.test(body) ? REDACTED : body;
  }
}

function redactHeaders(
  headers: Record<string, Nullish<string>>
): Record<string, Nullish<string>> {
  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      SENSITIVE_HEADERS.includes(key.toLowerCase()) ? REDACTED : value,
    ])
  );
}

// Compare names the way the router parses them, so `%72eset_key` or `reset%5Fkey` still match.
function isSensitiveQueryParam(name: string): boolean {
  let decodedName = name;

  try {
    decodedName = decodeURIComponent(name.replace(/\+/g, " "));
  } catch {
    // Malformed escapes are compared as-is.
  }

  return SENSITIVE_QUERY_PARAMS.has(decodedName.toLowerCase());
}

export function sanitizeUrl(url: string): string {
  const queryStart = url.indexOf("?");

  if (queryStart === -1) return url;

  const fragmentStart = url.indexOf("#", queryStart);
  const queryEnd = fragmentStart === -1 ? url.length : fragmentStart;

  const query = url
    .slice(queryStart + 1, queryEnd)
    .split("&")
    .map((pair) => {
      const name = pair.split("=")[0];

      return isSensitiveQueryParam(name) ? `${name}=${REDACTED}` : pair;
    })
    .join("&");

  return `${url.slice(0, queryStart + 1)}${query}${url.slice(queryEnd)}`;
}

export function sanitizeNetworkEntry<T extends SanitizableNetworkEntry>(
  entry: T
): T {
  return {
    ...entry,
    body: redactBody(entry.body),
    headers: redactHeaders(entry.headers),
    ...(entry.url ? { url: sanitizeUrl(entry.url) } : {}),
  };
}

// Shared by every LogRocket.init call (boot/logrocket and the analytics proxy) so no
// recording path starts without the sanitizers.
export const logrocketOptions = {
  browser: {
    urlSanitizer: sanitizeUrl,
  },
  dom: {
    baseHref: "__gcp_bucket_base_href__",
    release: "__commit_hash__",
  },
  // Sign-in, PIN change, sudo and token requests carry credentials in their bodies.
  network: {
    requestSanitizer: sanitizeNetworkEntry,
    responseSanitizer: sanitizeNetworkEntry,
  },
};
